/** @format */
import { css } from 'styled-components';

import {
    largeTablet,
    mobile,
    safeObjectFit,
    smallTablet,
} from '@toucan/client/global/application';
import { theme } from '@toucan/client/global/theme';

import { HeroBannerProps, HeroBannerVariant } from './index';

/**
 * Base styles for the hero banner component
 */
/* eslint-disable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */
const baseStyles = css`
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    min-height: 33vw; // 3:1 ratio with viewport width
    padding: 7.2rem 0;
    background: var(--colors-surface-inverse);
    text-align: center;

    img {
        width: 100%;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        ${safeObjectFit('cover')}
        height: 100%;
    }

    .hero-banner-content-wrapper {
        flex: 1;
        flex-basis: auto;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        position: relative;
        width: 100%;
        height: 100%;
    }

    .hero-banner-shape-container {
        position: absolute;
        bottom: -1px; /** stops it sometimes not covering the bottom of the image correctly */
        left: 0;
        right: 0;

        &::before {
            content: '';
            /* Basing the shapes height off of the width of element using padding trick */
            padding-bottom: 16%;
            display: block;
        }
    }

    .hero-banner-subcategories {
        border-top: 1px solid var(--colors-border-light);
        border-bottom: 1px solid var(--colors-border-light);
        li {
            :first-child a {
                margin-left: 0;
            }
            :last-child a {
                margin-right: 0;
            }
            a {
                margin: 0 var(--layout-spacing-12);
                white-space: nowrap;
            }
        }
        ${mobile(css`
            border-bottom: none;
        `)}
    }

    .hero-banner-shape {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        fill: var(--colors-surface-secondary);
        width: 100%;
        height: 100%;
        z-index: 2;
    }

    .hero-banner-heading,
    .hero-banner-content,
    .hero-banner-subheading {
        z-index: 3;
    }

    .hero-banner-heading {
        font-size: 4rem;
    }

    .hero-banner-subheading {
        ${theme.typography.body_large}
    }

    .hero-banner-subheading,
    .hero-banner-content {
        margin-bottom: var(--layout-spacing-12);
        > *:first-child {
            margin-top: 0;
        }

        > *:last-child {
            margin-bottom: 0;
        }
    }

    .hero-banner-breadcrumbs {
        position: absolute;
        top: 0;
        padding-top: 1.2rem;
        padding-bottom: 1.2rem;
        z-index: 3;
    }
`;
/* eslint-enable @mpb-com/mpb/use-design-tokens, @typescript-eslint/no-unsafe-argument -- Legacy code: do not use as an example. */

/**
 * Variant styles for the hero banner component
 *
 * @param {HeroBannerProps} props The hero banner properties object
 * @param {HeroBannerVariant} props.variant The variant of the hero
 * */
const variantStyles = (props: HeroBannerProps) => {
    switch (props.variant) {
        case HeroBannerVariant.LIGHT:
            return css`
                .hero-banner-heading,
                .hero-banner-subheading,
                .hero-banner-content {
                    color: var(--colors-text-inverse);
                }
            `;
        case HeroBannerVariant.GRAPHIC:
            return css`
                background: var(--colors-surface-inverse);
                text-align: center;

                img {
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: auto;
                    left: auto;
                    width: auto;
                    height: 100%;
                    object-fit: initial;
                }

                .hero-banner-content-wrapper {
                    color: var(--colors-text-inverse);
                }

                .hero-banner-subheading,
                .hero-banner-content {
                    max-width: 60rem;
                }
            `;

        case HeroBannerVariant.CATEGORY:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                display: -ms-grid;
                -ms-grid-columns: 1fr var(--layout-spacing-24) 1fr
                    var(--layout-spacing-24) 1fr;
                -ms-grid-rows: 6.4rem auto;
                text-align: initial;
                background: transparent;
                padding: 0 0 var(--layout-spacing-12);
                min-height: 0;
                display: grid;
                grid-template-columns: 1fr 1fr 1fr;
                grid-template-rows: 6.4rem auto;
                grid-column-gap: var(--layout-spacing-24);
                grid-template-areas:
                    'breadcrumbs breadcrumbs image'
                    'content content image';

                .hero-banner-heading {
                    margin-bottom: 1.5rem;
                }

                /* A wrapper the height of the semicircle */
                .hero-banner-container {
                    -ms-grid-column: 5;
                    -ms-grid-row: 1;
                    -ms-grid-row-span: 2;
                    -ms-grid-row-align: start;

                    position: relative;
                    grid-area: image;
                    align-self: flex-start;
                    overflow: hidden;

                    &::before {
                        content: '';
                        display: block;
                        padding-bottom: 50%;
                    }
                }

                /* A circle 'wrapper' around the image that hides the corners */
                .hero-banner-container-circle {
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border-radius: 50%;
                    overflow: hidden;

                    &::before {
                        content: '';
                        display: block;
                        padding-bottom: 100%;
                        background: var(--colors-surface-secondary);
                    }
                }

                img {
                    height: auto;
                    top: 35%; /* No need to make the image taller than what is visible */
                }

                .hero-banner-content {
                    font-size: 1.2rem;
                    color: var(--colors-text-primary);
                }

                .hero-banner-breadcrumbs {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 3;
                    -ms-grid-row: 1;

                    grid-area: breadcrumbs;
                    position: static;
                    margin: 1.5rem 0 0;
                    padding: 0;
                }

                .hero-banner-content-wrapper {
                    -ms-grid-column: 1;
                    -ms-grid-column-span: 3;
                    -ms-grid-row: 2;
                    -ms-grid-row-align: start;

                    grid-area: content;
                    align-self: flex-start;
                    padding: 0;
                }

                .hero-banner-shape-container {
                    display: none;
                }

                .hero-banner-subcategories {
                    margin-top: auto;
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

        case HeroBannerVariant.NO_BACKGROUND:
            return css`
                background: var(--colors-surface-primary);
                text-align: center;
                min-height: 0;
                padding: var(--layout-spacing-24) 0 var(--layout-spacing-24);
                border-bottom: 1px solid var(--colors-border-light);

                .hero-banner-breadcrumbs {
                    position: static;
                    padding: 0 var(--layout-spacing-12) 0
                        var(--layout-spacing-12);
                    margin-bottom: var(--layout-spacing-24);
                    text-align: left;
                    width: 100%;
                }

                .hero-banner-content {
                    max-width: 100%;
                }
            `;

        default:
            return css`
                .hero-banner-content-wrapper {
                    color: var(--colors-text-inverse);
                }
            `;
    }
};

/**
 * Mobile styles for the hero banner component
 */
const mobileStyles = (props: HeroBannerProps) => {
    switch (props.variant) {
        case HeroBannerVariant.NO_BACKGROUND:
            return css``;

        case HeroBannerVariant.CATEGORY:
            return css``;

        default:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                min-height: 100vw; // 1:1 ratio with width
                padding-top: 4.8rem;
                padding-bottom: 4.8rem;

                .hero-banner-shape-container {
                    &::before {
                        padding-bottom: 18.5%;
                    }
                }

                img {
                    right: ${
                        props.variant === HeroBannerVariant.GRAPHIC
                            ? '-75vw'
                            : '0'
                    };
                    opacity: ${
                        props.variant === HeroBannerVariant.GRAPHIC
                            ? '0.25'
                            : '1'
                    };
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
    }
};

/**
 * Small tablet styles for the hero banner component
 */
const smallTabletStyles = (props: HeroBannerProps) => {
    switch (props.variant) {
        case HeroBannerVariant.NO_BACKGROUND:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                padding-bottom: var(--layout-spacing-12);

                .hero-banner-content {
                    font-size: 1.2rem;
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */

        case HeroBannerVariant.CATEGORY:
            return css`
                -ms-grid-rows: 50vw auto;
                -ms-grid-columns: 1fr;
                grid-template-rows: none;
                grid-template-columns: 1fr;
                grid-template-areas:
                    'image'
                    'content';

                .hero-banner-breadcrumbs {
                    -ms-grid-column: 1;
                    -ms-grid-row: 1;
                    -ms-grid-row-align: start;
                    -ms-grid-column-align: start;

                    position: relative;
                    grid-area: image;
                    align-self: flex-start;
                    justify-self: flex-start;
                    padding: 0 var(--layout-spacing-12);
                }

                .hero-banner-shape-container {
                    -ms-grid-column: 1;
                    -ms-grid-row: 1;

                    grid-area: image;
                    display: block;
                }

                .hero-banner-container {
                    -ms-grid-column: 1;
                    -ms-grid-row: 1;

                    margin: 0;
                    background: var(--colors-surface-inverse);
                    min-height: 50vw; // 2:1 ratio with width

                    &::before {
                        display: none;
                    }
                }

                .hero-banner-content-wrapper {
                    -ms-grid-column: 1;
                    -ms-grid-row: 2;

                    padding: var(--layout-spacing-12) var(--layout-spacing-12) 0;
                }

                .hero-banner-container-circle {
                    position: static;
                    border-radius: 0;

                    &::before {
                        display: none;
                    }
                }

                img {
                    top: 0;
                    height: 100%;
                }

                .hero-banner-shape-container {
                    -ms-grid-row-align: end;
                    align-self: flex-end;
                    position: relative;

                    &::before {
                        padding-bottom: 18.5%;
                    }
                }
            `;

        default:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                min-height: 75vw; // 4:3 ratio with width
                padding-top: 7.2rem;
                padding-bottom: 7.2rem;

                img {
                    right: ${
                        props.variant === HeroBannerVariant.GRAPHIC
                            ? '-50vw'
                            : '0'
                    };
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
    }
};

/**
 * Large tablet styles for the hero banner component
 */
const largeTabletStyles = (props: HeroBannerProps) => {
    switch (props.variant) {
        case HeroBannerVariant.CATEGORY:
            return css`
                .hero-banner-container {
                    margin-right: -50%;
                }
            `;

        case HeroBannerVariant.NO_BACKGROUND:
            return css``;

        default:
            /* eslint-disable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
            return css`
                min-height: 50vw; // 2:1 ratio with width
                padding-top: 7.2rem;
                padding-bottom: 7.2rem;

                img {
                    right: ${
                        props.variant === HeroBannerVariant.GRAPHIC
                            ? '-25vw'
                            : '0'
                    };
                }
            `;
        /* eslint-enable @mpb-com/mpb/use-design-tokens -- Legacy code: do not use as an example. */
    }
};

/**
 * Desktop styles for the hero banner component
 */
const desktopStyles = (props: HeroBannerProps) => {
    switch (props.variant) {
        case HeroBannerVariant.CATEGORY:
            return css``;

        case HeroBannerVariant.NO_BACKGROUND:
            return css``;

        default:
            return css`
                min-height: 47.5rem;
            `;
    }
};

/**
 * The hero banner component styles
 *
 * @param {HeroBannerProps} props The hero banner properties object
 * */
export const styles = (props: HeroBannerProps) => css`
    ${baseStyles}
    ${variantStyles(props)}
    @media (min-width: 1440px) {
        ${desktopStyles(props)}
    }
    ${largeTablet(largeTabletStyles(props))}
    ${smallTablet(smallTabletStyles(props))}
    ${mobile(mobileStyles(props))}
`;
